import { useState } from 'react'
import type { GridKey, LocaleInfo } from '../api/types'
import { useUi } from '../store/ui'

export function BulkBar({
  keys,
  locales,
  onAiTranslate,
  onMarkReviewed,
  onAddTag,
  onBulkDeprecate,
  busy,
}: {
  keys: GridKey[]
  locales: LocaleInfo[]
  onAiTranslate: (keyIds: string[], localeCodes: string[]) => void
  onMarkReviewed: (translationIds: string[]) => void
  onAddTag: (keyIds: string[], tag: string) => void
  onBulkDeprecate: (keyIds: string[], deprecated: boolean) => void
  busy: boolean
}) {
  const selected = useUi((state) => state.selected)
  const clearSelection = useUi((state) => state.clearSelection)
  // `selected` is a flat, global-keyed map that can outlive the project it
  // was built on (see store/ui.ts). Deriving `chosen` — and therefore every
  // id this bar can ever submit — by filtering the CURRENTLY LOADED `keys`
  // is the defensive intersection: a stale id that is not part of this
  // view's rows is silently dropped here rather than shipped in a mutation.
  // Editor.tsx additionally clears the selection outright on navigation;
  // this is the second, independent line of defence.
  const chosen = keys.filter((key) => selected[key.id])
  const ids = chosen.map((key) => key.id)
  const targets = locales.filter((locale) => !locale.is_source)
  // null = "all target locales"; locales arrive after first render, so the
  // default cannot be captured in initial state.
  const [chosenCodes, setChosenCodes] = useState<string[] | null>(null)
  const localeCodes = chosenCodes ?? targets.map((item) => item.code)
  const [tag, setTag] = useState('')

  if (ids.length === 0) return null

  const reviewIds = chosen.flatMap((key) =>
    Object.values(key.cells)
      .flat()
      .filter((cell) => cell.needs_review && cell.origin === 'own' && cell.translation_id)
      .map((cell) => cell.translation_id as string),
  )
  // Only act on keys that would actually change state — mirrors the
  // server's own count-what-actually-changed contract (see
  // bulk_deprecate_keys), so the confirmation's number is never inflated by
  // already-deprecated/already-active rows silently along for the ride.
  const deprecatable = chosen.filter((key) => !key.deprecated).map((key) => key.id)
  const restorable = chosen.filter((key) => key.deprecated).map((key) => key.id)

  return (
    <div className="flex flex-wrap items-center gap-2 border-t border-blue-300 bg-blue-50 px-3 py-2 text-xs">
      <span className="font-semibold text-blue-900">
        {ids.length} key{ids.length === 1 ? '' : 's'} selected
      </span>
      <span className="h-4 w-px bg-blue-300" aria-hidden="true" />
      <fieldset className="flex items-center gap-2">
        <legend className="sr-only">Target locales for AI translation</legend>
        {targets.map((locale) => (
          <label key={locale.code} className="flex items-center gap-1 text-blue-900">
            <input
              type="checkbox"
              checked={localeCodes.includes(locale.code)}
              onChange={() =>
                setChosenCodes(
                  localeCodes.includes(locale.code)
                    ? localeCodes.filter((code) => code !== locale.code)
                    : [...localeCodes, locale.code],
                )
              }
            />
            <span className="font-mono text-2xs">{locale.code}</span>
          </label>
        ))}
      </fieldset>
      <button
        className="btn btn-primary"
        disabled={busy || localeCodes.length === 0}
        onClick={() => onAiTranslate(ids, localeCodes)}
      >
        ◆ AI-translate selected
      </button>
      <button
        className="btn"
        disabled={busy || reviewIds.length === 0}
        title={
          reviewIds.length === 0
            ? 'None of the selected cells is flagged for review'
            : `Clear the review flag on ${reviewIds.length} cell(s)`
        }
        onClick={() => onMarkReviewed(reviewIds)}
      >
        ✓ Mark reviewed ({reviewIds.length})
      </button>
      <span className="flex items-center gap-1">
        <label className="sr-only" htmlFor="bulk-tag">
          Tag to add to the selected keys
        </label>
        <input
          id="bulk-tag"
          className="field w-28 py-1 text-xs"
          placeholder="add tag…"
          value={tag}
          onChange={(event) => setTag(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && tag.trim()) {
              onAddTag(ids, tag.trim())
              setTag('')
            }
          }}
        />
        <button
          className="btn"
          disabled={busy || !tag.trim()}
          onClick={() => {
            onAddTag(ids, tag.trim())
            setTag('')
          }}
        >
          + Tag
        </button>
      </span>
      <button
        className="btn"
        disabled={busy || deprecatable.length === 0}
        title={
          deprecatable.length === 0
            ? 'Every selected key is already deprecated'
            : `Removes ${deprecatable.length} key(s) from every export immediately; translations` +
              ' and history are kept, and each can be restored individually or here in bulk.'
        }
        onClick={() => {
          if (
            window.confirm(
              `Deprecate ${deprecatable.length} key(s)? Removed from every export immediately — ` +
                'apps stop receiving them on their next pull. Translations and history are kept, ' +
                'and they can be restored anytime.',
            )
          ) {
            onBulkDeprecate(deprecatable, true)
          }
        }}
      >
        Deprecate ({deprecatable.length})
      </button>
      <button
        className="btn"
        disabled={busy || restorable.length === 0}
        title={
          restorable.length === 0
            ? 'None of the selected keys is deprecated'
            : `Brings ${restorable.length} key(s) back into every export on the next pull`
        }
        onClick={() => onBulkDeprecate(restorable, false)}
      >
        Restore ({restorable.length})
      </button>
      <div className="flex-1" />
      <button className="btn" onClick={clearSelection}>
        Clear selection
      </button>
    </div>
  )
}
